import baseAnimateScrollTo, { type IUserOptions, type TCoords } from 'animated-scroll-to';
import { confetti as domConfetti, ConfettiConfig } from 'dom-confetti';

/**
 * Whether the user asked for less motion. Stylesheets turn off CSS animations
 * and transitions then, but scrolling and animations started from script
 * need to check for themselves.
 */
export function prefersReducedMotion(): boolean {
    return matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/** Smooth scrolling for `scrollIntoView` and `scrollTo`, unless motion is reduced. */
export function smoothScroll(): ScrollBehavior {
    return prefersReducedMotion() ? 'instant' : 'smooth';
}

/** Animate scrolling, jumping straight to the target when motion is reduced. */
export const animateScrollTo: typeof baseAnimateScrollTo = (
    target: number | TCoords | Element,
    options?: IUserOptions,
) =>
    // The library overloads each kind of target separately.
    baseAnimateScrollTo(
        target as Element,
        prefersReducedMotion() ? { ...options, minDuration: 0, maxDuration: 0 } : options,
    );

export default function animate(el: Element, className: string): void {
    el.classList.add(className);
    el.addEventListener('animationend', () => el.classList.remove(className), {
        once: true,
    });
}

export function pulse(el: Element | null | undefined, scale = 1.1, duration = 200): void {
    if (prefersReducedMotion()) {
        return;
    }

    el?.animate([{ transform: `scale(${scale})`, offset: 0.5 }], {
        duration,
        easing: 'ease-in-out',
    });
}

export function confetti(el: HTMLElement, config?: ConfettiConfig): void {
    if (prefersReducedMotion()) {
        return;
    }

    if (getComputedStyle(el).position === 'static') {
        el.style.position = 'relative';
    }

    const confettiEl = document.createElement('div');

    Object.assign(confettiEl.style, {
        position: 'absolute',
        top: '50%',
        left: '50%',
        zIndex: 'var(--z-index-overlay)',
    });

    el.appendChild(confettiEl);

    domConfetti(
        confettiEl,
        config || {
            colors: ['var(--color-success)'],
            duration: 2000,
            angle: 90,
            spread: 90,
            startVelocity: 20,
            elementCount: 20,
            dragFriction: 0.1,
            width: '7px',
            height: '7px',
        },
    );
}
